/**
 * The one vocabulary every summoned surface and every control that must yield
 * to one speaks: a role attribute on the surface, and two comparisons against
 * the LAYER scale that owns what a role outranks.
 *
 * `aria-modal` is an assistive-technology contract, not a coordination
 * channel — it says nothing about rank, and the rail carries it in mobile mode
 * without being a summoned surface at all. Reading the role attribute instead
 * lets a caller name the floor it stands on.
 *
 * @module utils/overlayLayers
 */

import { LAYER, layerZIndex } from '@/theme/layers'

export const OVERLAY_LAYER_ATTR = 'data-overlay-layer'

/**
 * Roles a summoned, dismissable surface renders at. Popover and tooltip are
 * transient Radix layers and never set the attribute.
 */
export type OverlayRole = 'drawer' | 'overlay' | 'modal'

/** The floor a caller stands on when it asks what is above it. */
export type OverlayFloor = 'float' | OverlayRole

/**
 * Rank order, highest first. Both queries walk it and stop at their first
 * answer, so a pointerdown or a Tab keystroke pays one short query rather than
 * collecting and sorting every open surface in the document.
 */
const ROLES_BY_RANK: readonly OverlayRole[] = ['modal', 'overlay', 'drawer']

function selectorFor(role: OverlayRole): string {
  return `[${OVERLAY_LAYER_ATTR}="${role}"]`
}

const ANY_ROLE_SELECTOR = ROLES_BY_RANK.map(selectorFor).join(', ')

/** Rank comes from the scale, so the vocabulary and the stacking order cannot disagree. */
function rankOf(floor: OverlayFloor): number {
  return layerZIndex(LAYER[floor])
}

function isOverlayRole(value: string | null): value is OverlayRole {
  return ROLES_BY_RANK.includes(value as OverlayRole)
}

/**
 * The attribute a setter spreads onto its root, typed against OverlayRole so a
 * misspelt value is a compile error rather than a stacking bug.
 */
export function overlayLayerAttr(role: OverlayRole): {
  [OVERLAY_LAYER_ATTR]: OverlayRole
} {
  return { [OVERLAY_LAYER_ATTR]: role }
}

/**
 * The role `element` renders at, or null when nothing summoned it.
 *
 * Read from the nearest surface at or above `element`, because the region a
 * focus trap holds is usually a child of the root that carries the attribute:
 * the modal traps its panel, which sits inside the overlay that names the role.
 */
export function overlayRoleOf(element: Element): OverlayRole | null {
  const surface = element.closest(ANY_ROLE_SELECTOR)
  const role = surface?.getAttribute(OVERLAY_LAYER_ATTR) ?? null
  return isOverlayRole(role) ? role : null
}

/**
 * The first open surface of `role` that is not `except`.
 *
 * `querySelector` answers the common call — which passes no exclusion — with
 * one short query; only an excluded first match makes it walk the rest.
 */
function openSurface(
  role: OverlayRole,
  except?: Element | null
): HTMLElement | null {
  const first = document.querySelector<HTMLElement>(selectorFor(role))
  if (first === null || first !== except) return first
  for (const element of document.querySelectorAll<HTMLElement>(
    selectorFor(role)
  )) {
    if (element !== except) return element
  }
  return null
}

/**
 * True when an open surface strictly outranks `floor`.
 *
 * Strict is what a caller asking "is anything above me" wants: the rail, the
 * global shortcut and the waypoint hook stand on `float` and must yield to
 * anything summoned above it, while the slide-over stands on `overlay` and
 * yields to a modal only. A surface at the caller's own rank is the caller
 * itself or its sibling, and neither takes the key away.
 */
export function hasOverlayAbove(
  floor: OverlayFloor,
  except?: Element | null
): boolean {
  const floorRank = rankOf(floor)
  for (const role of ROLES_BY_RANK) {
    // The roles descend by rank: once one fails the strict test, so does every
    // role after it.
    if (rankOf(role) <= floorRank) return false
    if (openSurface(role, except) !== null) return true
  }
  return false
}

/**
 * The open surface that covers `container`: it does not contain `container`
 * and ranks at or above `overlayRoleOf(container) ?? 'float'`. Null when none.
 *
 * Inclusive where `hasOverlayAbove` is strict, because the focus trap asks a
 * different question: two surfaces of the same rank — a confirmation over an
 * edit modal — do cover each other, and the later one owns focus. Among equals
 * the later in DOM order wins, which is the later portal.
 */
export function coveringOverlay(container: Element): HTMLElement | null {
  const floorRank = rankOf(overlayRoleOf(container) ?? 'float')
  for (const role of ROLES_BY_RANK) {
    if (rankOf(role) < floorRank) return null
    const open = document.querySelectorAll<HTMLElement>(selectorFor(role))
    const topmost = open[open.length - 1]
    if (topmost === undefined) continue
    // Only the topmost surface of a rank can cover, and when that is the
    // container's own surface nothing at this rank does: every other surface
    // of the rank is earlier in DOM order, so it paints underneath.
    return topmost.contains(container) ? null : topmost
  }
  return null
}
